import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useEffect } from "react";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { HelmetProvider } from "react-helmet-async";
import { pingIndexNowOncePerDay } from "@/lib/indexnow";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AdminVideos from "./pages/AdminVideos";
import AdminOrders from "./pages/AdminOrders";
import Impressum from "./pages/Impressum";
import Privacy from "./pages/Privacy";
import Order from "./pages/Order";
import OrderSuccess from "./pages/OrderSuccess";
import AIUGCGenerator from "./pages/AIUGCGenerator";
import HireUGC from "./pages/HireUGC";
import UGCDeutschland from "./pages/UGCDeutschland";
import UGCKaufen from "./pages/UGCKaufen";
import UGCAgentur from "./pages/UGCAgentur";
import UGCCreatorBuchen from "./pages/UGCCreatorBuchen";
import KIUGCGenerator from "./pages/KIUGCGenerator";
import UGCWerbevideo from "./pages/UGCWerbevideo";
// English Landing Pages
import BuyUGC from "./pages/BuyUGC";
import UGCAds from "./pages/UGCAds";
import AIAvatarUGC from "./pages/AIAvatarUGC";
import TikTokUGCAds from "./pages/TikTokUGCAds";
import UGCWithoutFilming from "./pages/UGCWithoutFilming";
// City Landing Pages
import UGCAgenturBerlin from "./pages/UGCAgenturBerlin";
import UGCAgenturMuenchen from "./pages/UGCAgenturMuenchen";
import UGCAgenturKoeln from "./pages/UGCAgenturKoeln";
import UGCAgenturHamburg from "./pages/UGCAgenturHamburg";
import UGCAgenturWien from "./pages/UGCAgenturWien";
import UGCAgenturZuerich from "./pages/UGCAgenturZuerich";
// Blog
import Blog from "./pages/Blog";
import WhatIsAiUgc from "./pages/blog/WhatIsAiUgc";
import AiUgcVsTraditional from "./pages/blog/AiUgcVsTraditional";
import WhatIsFuel72 from "./pages/blog/WhatIsFuel72";
import WhyVisibilityMatters2026 from "./pages/blog/WhyVisibilityMatters2026";
import CaseStudies from "./pages/blog/CaseStudies";
import HowToBoostBusiness from "./pages/blog/HowToBoostBusiness";
import WasIstAiUgc from "./pages/blog/WasIstAiUgc";
import UgcVideoErstellen from "./pages/blog/UgcVideoErstellen";
import Fuel72Erfahrungen from "./pages/blog/Fuel72Erfahrungen";
// New German Blog Articles
import UGCAgenturVsFreelancer from "./pages/blog/UGCAgenturVsFreelancer";
import UGCKostenPreise2026 from "./pages/blog/UGCKostenPreise2026";
import KIAvatarVideos from "./pages/blog/KIAvatarVideos";
import TikTokReelsAdsErstellen from "./pages/blog/TikTokReelsAdsErstellen";
// New English Blog Articles
import BuyUGCVideosGuide from "./pages/blog/BuyUGCVideosGuide";
import UGCForMetaAds from "./pages/blog/UGCForMetaAds";
import AIUGCVsInfluencer from "./pages/blog/AIUGCVsInfluencer";
import BestAIUGCTools from "./pages/blog/BestAIUGCTools";
// Comparison Blog Articles
import Fuel72VsSynthesia from "./pages/blog/Fuel72VsSynthesia";
import Fuel72VsHeygen from "./pages/blog/Fuel72VsHeygen";
// New Pages
import FAQ from "./pages/FAQ";
import Pricing from "./pages/Pricing";
import Reviews from "./pages/Reviews";
import Portfolio from "./pages/Portfolio";
// English City Pages
import UGCNewYork from "./pages/UGCNewYork";
import UGCLosAngeles from "./pages/UGCLosAngeles";
import UGCLondon from "./pages/UGCLondon";
import UGCSydney from "./pages/UGCSydney";
import UGCToronto from "./pages/UGCToronto";
import ScrollToTop from "./components/ScrollToTop";

const queryClient = new QueryClient();

const App = () => {
  useEffect(() => {
    pingIndexNowOncePerDay();
  }, []);

  return (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <TooltipProvider>
          {/* Premium visual polish overlays */}
          <div className="film-grain" aria-hidden="true" />
          <div className="page-vignette" aria-hidden="true" />
          
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/order" element={<Order />} />
              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/videos" element={<AdminVideos />} />
              <Route path="/admin/orders" element={<AdminOrders />} />
              <Route path="/impressum" element={<Impressum />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/order-success" element={<OrderSuccess />} />
              {/* SEO Landing Pages - English */}
              <Route path="/ai-ugc-video-generator" element={<AIUGCGenerator />} />
              <Route path="/hire-ugc" element={<HireUGC />} />
              <Route path="/buy-ugc" element={<BuyUGC />} />
              <Route path="/ugc-ads" element={<UGCAds />} />
              <Route path="/ai-avatar-ugc" element={<AIAvatarUGC />} />
              <Route path="/tiktok-ugc-ads" element={<TikTokUGCAds />} />
              <Route path="/ugc-without-filming" element={<UGCWithoutFilming />} />
              {/* SEO Landing Pages - German */}
              <Route path="/ugc-deutschland" element={<UGCDeutschland />} />
              <Route path="/ugc-kaufen" element={<UGCKaufen />} />
              <Route path="/ugc-agentur" element={<UGCAgentur />} />
              <Route path="/ugc-creator-buchen" element={<UGCCreatorBuchen />} />
              <Route path="/ki-ugc-generator" element={<KIUGCGenerator />} />
              <Route path="/ugc-werbevideo" element={<UGCWerbevideo />} />
              {/* City Landing Pages */}
              <Route path="/ugc-agentur-berlin" element={<UGCAgenturBerlin />} />
              <Route path="/ugc-agentur-muenchen" element={<UGCAgenturMuenchen />} />
              <Route path="/ugc-agentur-koeln" element={<UGCAgenturKoeln />} />
              <Route path="/ugc-agentur-hamburg" element={<UGCAgenturHamburg />} />
              <Route path="/ugc-agentur-wien" element={<UGCAgenturWien />} />
              <Route path="/ugc-agentur-zuerich" element={<UGCAgenturZuerich />} />
              {/* Blog */}
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/what-is-ai-ugc" element={<WhatIsAiUgc />} />
              <Route path="/blog/ai-ugc-vs-traditional-ugc" element={<AiUgcVsTraditional />} />
              <Route path="/blog/what-is-fuel72" element={<WhatIsFuel72 />} />
              <Route path="/blog/why-online-visibility-matters-2026" element={<WhyVisibilityMatters2026 />} />
              <Route path="/blog/case-studies-ugc-results" element={<CaseStudies />} />
              <Route path="/blog/how-to-boost-business-with-ai-ugc" element={<HowToBoostBusiness />} />
              <Route path="/blog/was-ist-ai-ugc" element={<WasIstAiUgc />} />
              <Route path="/blog/ugc-videos-erstellen-ohne-filmen" element={<UgcVideoErstellen />} />
              <Route path="/blog/fuel72-erfahrungen" element={<Fuel72Erfahrungen />} />
              {/* New German Blog Articles */}
              <Route path="/blog/ugc-agentur-vs-freelancer" element={<UGCAgenturVsFreelancer />} />
              <Route path="/blog/ugc-kosten-preise-2026" element={<UGCKostenPreise2026 />} />
              <Route path="/blog/ki-avatar-videos-marketing" element={<KIAvatarVideos />} />
              <Route path="/blog/tiktok-reels-ads-erstellen" element={<TikTokReelsAdsErstellen />} />
              {/* New English Blog Articles */}
              <Route path="/blog/buy-ugc-videos-guide" element={<BuyUGCVideosGuide />} />
              <Route path="/blog/ugc-for-meta-ads" element={<UGCForMetaAds />} />
              <Route path="/blog/ai-ugc-vs-influencer-marketing" element={<AIUGCVsInfluencer />} />
              <Route path="/blog/best-ai-ugc-generator-tools" element={<BestAIUGCTools />} />
              {/* Comparison Blog Articles */}
              <Route path="/blog/fuel72-vs-synthesia" element={<Fuel72VsSynthesia />} />
              <Route path="/blog/fuel72-vs-heygen" element={<Fuel72VsHeygen />} />
              {/* New Pages */}
              <Route path="/faq" element={<FAQ />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/reviews" element={<Reviews />} />
              {/* English City Pages */}
              <Route path="/ugc-new-york" element={<UGCNewYork />} />
              <Route path="/ugc-los-angeles" element={<UGCLosAngeles />} />
              <Route path="/ugc-london" element={<UGCLondon />} />
              <Route path="/ugc-sydney" element={<UGCSydney />} />
              <Route path="/ugc-toronto" element={<UGCToronto />} />
              <Route path="/portfolio" element={<Portfolio />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </LanguageProvider>
    </QueryClientProvider>
  </HelmetProvider>
  );
};

export default App;
